import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import LogList from './LogList';
import BackfillModal from './BackfillModal';

function LogView({ token, socket, apiUrl, user, eventType = 'WFD' }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contestFilter, setContestFilter] = useState(eventType);
  const [backfillModal, setBackfillModal] = useState('idle'); // 'idle' | 'confirm' | 'running' | 'done'
  const [backfillResult, setBackfillResult] = useState(null);

  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setLoading(false);
    }
  }, [apiUrl, token]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Keep selector in sync when the active contest changes externally
  useEffect(() => {
    setContestFilter(eventType);
  }, [eventType]);

  useEffect(() => {
    if (socket) {
      const handleNewContact = (contact) => {
        setContacts(prev => [contact, ...prev]);
      };

      const handleContactUpdated = (contact) => {
        setContacts(prev => prev.map(c => c.id === contact.id ? contact : c));
      };

      const handleContactDeleted = ({ id }) => {
        setContacts(prev => prev.filter(c => c.id !== id));
      };

      socket.on('newContact', handleNewContact);
      socket.on('contactUpdated', handleContactUpdated);
      socket.on('contactDeleted', handleContactDeleted);

      return () => {
        socket.off('newContact', handleNewContact);
        socket.off('contactUpdated', handleContactUpdated);
        socket.off('contactDeleted', handleContactDeleted);
      };
    }
  }, [socket]);

  const handleEdit = async (id, formData) => {
    try {
      await axios.put(`${apiUrl}/api/contacts/${id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Error updating contact:', err);
      throw err;
    }
  };

  const handleBackfillConfirm = async () => {
    setBackfillModal('running');
    try {
      const response = await axios.post(
        `${apiUrl}/api/contacts/backfill-locations`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBackfillResult(response.data);
      setBackfillModal('done');
      // Refresh contacts list so updated rows reflect immediately
      fetchContacts();
    } catch (err) {
      console.error('Backfill error:', err);
      setBackfillResult({ error: 'Backfill failed. Check server logs.' });
      setBackfillModal('done');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    
    try {
      await axios.delete(`${apiUrl}/api/contacts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Error deleting contact:', err);
    }
  };

  if (loading) {
    return (
      <main className="main-content">
        <div className="card">
          <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>Loading contacts...</p>
        </div>
      </main>
    );
  }

  const filteredContacts = contacts.filter(c => {
    // Support contacts without contest_type (legacy) by inferring from class
    const ct = c.contest_type || (/^\d+[ABCDEF]$/.test(c.class) ? 'ARFD' : 'WFD');
    return ct === contestFilter;
  });

  return (
    <main className="main-content" style={{ maxWidth: '1600px' }}>
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <h2 className="card-title" style={{ margin: 0 }}>
            Full Log ({filteredContacts.length} of {contacts.length} contacts)
          </h2>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              className={`btn btn-small ${contestFilter === 'WFD' ? '' : 'btn-secondary'}`}
              onClick={() => setContestFilter('WFD')}
            >
              WFD ({contacts.filter(c => (c.contest_type || (/^\d+[ABCDEF]$/.test(c.class) ? 'ARFD' : 'WFD')) === 'WFD').length})
            </button>
            <button
              className={`btn btn-small ${contestFilter === 'ARFD' ? '' : 'btn-secondary'}`}
              onClick={() => setContestFilter('ARFD')}
            >
              ARRL FD ({contacts.filter(c => (c.contest_type || (/^\d+[ABCDEF]$/.test(c.class) ? 'ARFD' : 'WFD')) === 'ARFD').length})
            </button>
            <button
              className="btn btn-small btn-secondary"
              onClick={() => setBackfillModal('confirm')}
              title="Attempt to fill in missing location data from callsign lookup"
            >
              Backfill Locations
            </button>
          </div>
        </div>
        <LogList 
          contacts={filteredContacts}
          onEdit={handleEdit}
          onDelete={handleDelete}
          token={token}
          apiUrl={apiUrl}
          user={user}
          eventType={contestFilter}
        />
      </div>

      {backfillModal !== 'idle' && (
        <BackfillModal
          phase={backfillModal}
          result={backfillResult}
          onConfirm={handleBackfillConfirm}
          onClose={() => { setBackfillModal('idle'); setBackfillResult(null); }}
        />
      )}
    </main>
  );
}

export default LogView;
